import { useState, useCallback } from 'react';
import { useAuth, UserRole } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import samplesService, { Sample, SampleStatus } from '../services/samplesService';

interface QRWorkflowState {
  loading: boolean;
  error: string | null;
  sample: Sample | null;
  nextAction: 'reception' | 'preparation' | 'analysis' | 'quality' | 'view' | null;
}

export const useQRWorkflow = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [state, setState] = useState<QRWorkflowState>({
    loading: false,
    error: null,
    sample: null,
    nextAction: null,
  });

  const processQRScan = useCallback(async (qrData: string) => {
    setState(prev => ({ ...prev, loading: true, error: null }));

    try {
      let sample: Sample;
      
      // Try to get sample by QR data or sample ID
      try {
        sample = await samplesService.scanQRCode(qrData);
      } catch {
        // Fallback: try as sample ID
        sample = await samplesService.getSampleBySampleId(qrData);
      }

      // Determine next action based on user role and sample status
      const nextAction = determineNextAction(user?.role, sample.statut);
      
      // Auto-update location based on user role
      await updateSampleLocation(sample, user?.role);

      setState({
        loading: false,
        error: null,
        sample,
        nextAction,
      });

      return { sample, nextAction };
    } catch (error) {
      const errorMessage = 'QR Code invalide ou échantillon non trouvé';
      setState(prev => ({
        ...prev,
        loading: false,
        error: errorMessage,
        sample: null,
        nextAction: null,
      }));
      throw new Error(errorMessage);
    }
  }, [user?.role]);

  const determineNextAction = (userRole?: UserRole, sampleStatus?: SampleStatus): QRWorkflowState['nextAction'] => {
    if (!userRole || !sampleStatus) return 'view';

    switch (userRole) {
      case UserRole.SAMPLE_RECEIVER:
        if (sampleStatus === SampleStatus.RECEPTION || sampleStatus === SampleStatus.EN_ATTENTE) {
          return 'reception';
        }
        return 'view';

      case UserRole.TECHNICIAN_PREP:
        if (sampleStatus === SampleStatus.RECEPTION) {
          return 'preparation';
        }
        return 'view';

      case UserRole.ANALYST:
        if (sampleStatus === SampleStatus.PREPARATION_MECANIQUE) {
          return 'analysis';
        }
        return 'view';

      case UserRole.QUALITY_MANAGER:
        if (sampleStatus === SampleStatus.ANALYSE_CHIMIQUE) {
          return 'quality';
        }
        return 'view';

      case UserRole.ADMIN:
      case UserRole.LABORATORY_MANAGER:
        // Admins can access any workflow step
        switch (sampleStatus) {
          case SampleStatus.RECEPTION:
          case SampleStatus.EN_ATTENTE:
            return 'reception';
          case SampleStatus.PREPARATION_MECANIQUE:
            return 'analysis';
          case SampleStatus.ANALYSE_CHIMIQUE:
            return 'quality';
          default:
            return 'view';
        }

      default:
        return 'view';
    }
  };

  const updateSampleLocation = async (sample: Sample, userRole?: UserRole) => {
    if (!userRole) return;

    let newLocation: string | null = null;

    switch (userRole) {
      case UserRole.SAMPLE_RECEIVER:
        newLocation = 'RECEPTION';
        break;
      case UserRole.TECHNICIAN_PREP:
        newLocation = 'PREPARATION';
        break;
      case UserRole.ANALYST:
        newLocation = 'ANALYSE';
        break;
      case UserRole.QUALITY_MANAGER:
        newLocation = 'STOCKAGE_TEMP';
        break;
    }

    if (newLocation && sample.localisation_stockage !== newLocation) {
      try {
        await samplesService.updateSample(sample.id, {
          localisation_stockage: newLocation,
        });
      } catch (error) {
        console.warn('Failed to update sample location:', error);
      }
    }
  };

  const executeWorkflowAction = useCallback((sample: Sample, action: QRWorkflowState['nextAction']) => {
    switch (action) {
      case 'reception':
        navigate(`/reception?sample=${sample.id}`);
        break;
      case 'preparation':
        navigate(`/preparation?sample=${sample.id}`);
        break;
      case 'analysis':
        navigate(`/analysis?sample=${sample.id}`);
        break;
      case 'quality':
        navigate(`/samples/validation?sample=${sample.id}`);
        break;
      case 'view':
      default:
        navigate(`/samples/${sample.id}`);
        break;
    }
  }, [navigate]);

  const getWorkflowMessage = (sample: Sample, action: QRWorkflowState['nextAction']) => {
    const sampleId = sample.sample_id;
    
    switch (action) {
      case 'reception':
        return `Échantillon ${sampleId} prêt pour la réception. Cliquez pour ouvrir le formulaire de réception.`;
      case 'preparation':
        return `Échantillon ${sampleId} prêt pour la préparation mécanique. Cliquez pour commencer la préparation.`;
      case 'analysis':
        return `Échantillon ${sampleId} prêt pour l'analyse chimique. Cliquez pour saisir les résultats d'analyse.`;
      case 'quality':
        return `Échantillon ${sampleId} prêt pour validation qualité. Cliquez pour approuver ou rejeter.`;
      case 'view':
        return `Affichage de l'échantillon ${sampleId}. Statut actuel: ${sample.statut}`;
      default:
        return `Échantillon ${sampleId} scanné avec succès.`;
    }
  };

  const canUserProcessSample = (userRole?: UserRole, sampleStatus?: SampleStatus): boolean => {
    if (!userRole || !sampleStatus) return false;

    switch (userRole) {
      case UserRole.SAMPLE_RECEIVER:
        return sampleStatus === SampleStatus.RECEPTION || sampleStatus === SampleStatus.EN_ATTENTE;
      case UserRole.TECHNICIAN_PREP:
        return sampleStatus === SampleStatus.RECEPTION;
      case UserRole.ANALYST:
        return sampleStatus === SampleStatus.PREPARATION_MECANIQUE;
      case UserRole.QUALITY_MANAGER:
        return sampleStatus === SampleStatus.ANALYSE_CHIMIQUE;
      case UserRole.ADMIN:
      case UserRole.LABORATORY_MANAGER:
        return true; // Admins can always process
      default:
        return false;
    }
  };

  const reset = useCallback(() => {
    setState({
      loading: false,
      error: null,
      sample: null,
      nextAction: null,
    });
  }, []);

  return {
    ...state,
    processQRScan,
    executeWorkflowAction,
    getWorkflowMessage,
    canUserProcessSample,
    reset,
  };
}; 